<template>
  <div class="video-player-container" :style="containerStyle">
    <video-player
      ref="videoPlayer"
      class="video-player vjs-custom-skin"
      :playsinline="playsinline"
      :options="playerOptions"
      :events="playerEvents"
      @ready="onPlayerReady"
      @play="onPlayerPlay"
      @pause="onPlayerPause"
      @ended="onPlayerEnded"
      @timeupdate="onPlayerTimeupdate"
      @waiting="onPlayerWaiting"
      @playing="onPlayerPlaying"
      @error="onPlayerError"
    />
    
    <!-- 自定义加载提示 -->
    <div v-if="loading" class="video-loading">
      <div class="loading-spinner"></div>
    </div>
  </div>
</template>

<script>
import 'videojs-contrib-hls'

export default {
  name: 'VideoPlay',
  props: {
    // 视频地址
    videoUrl: {
      type: String,
      required: true
    },
    // 视频封面
    poster: {
      type: String,
      default: ''
    },
    // 视频类型
    videoType: {
      type: String,
      default: 'video/mp4', // 可选 'application/x-mpegURL' (m3u8)
    },
    // 是否自动播放
    autoplay: {
      type: Boolean,
      default: false
    },
    // 是否静音
    muted: {
      type: Boolean,
      default: false
    },
    // 是否循环播放
    loop: {
      type: Boolean,
      default: false
    },
    // 是否显示控制栏
    controls: {
      type: Boolean,
      default: true
    },
    // 播放速度选项
    playbackRates: {
      type: Array,
      default: () => [0.5, 1, 1.5, 2]
    },
    // 组件宽度
    width: {
      type: [String, Number],
      default: '100%'
    },
    // 组件高度
    height: {
      type: [String, Number],
      default: 'auto'
    },
    // 是否自适应容器
    fluid: {
      type: Boolean,
      default: true
    },
    // 宽高比
    aspectRatio: {
      type: String,
      default: '16:9'
    },
    // 内联播放（移动端）
    playsinline: {
      type: Boolean,
      default: true
    }
  },
  
  data() {
    return {
      loading: false,
      player: null,
      currentTime: 0,
      duration: 0
    }
  },

  computed: {
    // 容器样式
    containerStyle() {
      return {
        width: this.width,
        height: this.height
      }
    },

    // 播放器配置
    playerOptions() {
      return {
        // 播放速度
        playbackRates: this.playbackRates,
        // 自动播放
        autoplay: this.autoplay,
        // 是否静音
        muted: this.muted,
        // 循环播放
        loop: this.loop,
        // 预加载
        preload: 'auto',
        // 语言
        language: 'zh-CN',
        // 宽高比
        aspectRatio: this.aspectRatio,
        // 流体布局
        fluid: this.fluid,
        // 视频源 [citation:1][citation:4]
        sources: [{
          type: this.videoType,
          src: this.videoUrl
        }],
        // 封面图
        poster: this.poster,
        // 不支持提示
        notSupportedMessage: '此视频暂无法播放，请稍后再试',
        // 控制栏配置 [citation:7]
        controlBar: {
          timeDivider: true,
          durationDisplay: true,
          remainingTimeDisplay: false,
          fullscreenToggle: true,
          // 画中画按钮（Chrome支持）
          pictureInPictureToggle: true
        },
        // HLS 配置
        html5: {
          hls: {
            withCredentials: false,
            overrideNative: true
          },
          nativeAudioTracks: false,
          nativeVideoTracks: false
        }
      }
    },

    // 需要监听的事件列表
    playerEvents() {
      return ['ready', 'play', 'pause', 'ended', 'timeupdate', 'waiting', 'playing', 'error']
    }
  },

  watch: {
    // 监听视频地址变化
    videoUrl: {
      handler(newVal) {
        if (this.player && newVal) {
          this.player.src({ src: newVal, type: this.videoType })
          this.player.load()
          this.$emit('src-changed', newVal)
        }
      },
      immediate: false
    },
    
    // 监听封面变化
    poster(newVal) {
      if (this.player && newVal) {
        this.player.poster(newVal)
      }
    },

    // 监听静音状态
    muted(newVal) {
      if (this.player) {
        this.player.muted(newVal)
      }
    },

    // 监听自动播放
    autoplay(newVal) {
      if (this.player) {
        this.player.autoplay(newVal)
      }
    }
  },

  mounted() {
    // 组件挂载后初始化播放器
    this.$nextTick(() => {
      this.initPlayer()
    })
  },

  beforeDestroy() {
    this.destroyPlayer()
  },

  methods: {
    // 初始化播放器
    initPlayer() {
      this.player = this.$refs.videoPlayer.player
    },

    // 销毁播放器
    destroyPlayer() {
      if (this.player) {
        this.player.dispose()
        this.player = null
      }
    },

    // 播放器准备就绪
    onPlayerReady(player) {
      this.player = player
      this.$emit('ready', player)
    },

    // 开始播放
    onPlayerPlay() {
      this.loading = false
      this.$emit('play')
    },

    // 暂停播放
    onPlayerPause() {
      this.$emit('pause')
    },

    // 播放结束
    onPlayerEnded() {
      this.$emit('ended')
    },

    // 时间更新
    onPlayerTimeupdate(event) {
      this.currentTime = this.player.currentTime()
      this.duration = this.player.duration()
      this.$emit('timeupdate', {
        currentTime: this.currentTime,
        duration: this.duration
      })
    },

    // 缓冲等待
    onPlayerWaiting() {
      this.loading = true
      this.$emit('waiting')
    },

    // 开始播放（缓冲结束）
    onPlayerPlaying() {
      this.loading = false
      this.$emit('playing')
    },

    // 播放错误
    onPlayerError(error) {
      this.loading = false
      console.error('视频播放错误:', error)
      this.$emit('error', error)
    },

    // ---------- 暴露给父组件的方法 ----------
    // 播放
    play() {
      if (this.player) {
        this.player.play()
      }
    },

    // 暂停
    pause() {
      if (this.player) {
        this.player.pause()
      }
    },

    // 切换播放/暂停
    togglePlay() {
      if (this.player) {
        if (this.player.paused()) {
          this.player.play()
        } else {
          this.player.pause()
        }
      }
    },

    // 跳转到指定时间
    seekTo(time) {
      if (this.player) {
        this.player.currentTime(time)
      }
    },

    // 设置音量
    setVolume(volume) {
      if (this.player) {
        this.player.volume(volume)
      }
    },

    // 设置静音
    setMuted(muted) {
      if (this.player) {
        this.player.muted(muted)
      }
    },

    // 进入全屏
    requestFullscreen() {
      if (this.player) {
        this.player.requestFullscreen()
      }
    },

    // 退出全屏
    exitFullscreen() {
      if (this.player) {
        this.player.exitFullscreen()
      }
    },

    // 获取当前时间
    getCurrentTime() {
      return this.player ? this.player.currentTime() : 0
    },

    // 获取总时长
    getDuration() {
      return this.player ? this.player.duration() : 0
    }
  }
}
</script>

<style scoped>
.video-player-container {
  position: relative;
  background-color: #000;
  overflow: hidden;
}

.video-player {
  width: 100%;
  height: 100%;
}

/* 自定义加载动画 */
.video-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #ff0000;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 覆盖 video.js 默认样式 - 可自定义 */
:deep(.video-js .vjs-big-play-button) {
  background-color: rgba(255, 0, 0, 0.8);
  border-color: #ff0000;
}

:deep(.video-js .vjs-play-progress) {
  background-color: #ff0000;
}

:deep(.video-js .vjs-volume-level) {
  background-color: #ff0000;
}
</style>